import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Paperclip,
  Plus,
  Scale,
  Layers,
  Wheat,
  Syringe,
  Receipt,
  Tag,
  FolderLock,
  FileSpreadsheet,
  ArrowDownLeft,
  Skull,
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  Edit,
  Pill,
  Trash2,
  AlertCircle,
  Truck,
  Warehouse,
  Printer,
  Eye,
  Camera,
  Lock,
  Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { TabularNumber } from '@/components/common/TabularNumber';
import { DocumentAttachmentModal } from '@/components/documents/DocumentAttachmentModal';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { DocumentUploader } from '@/components/documents/DocumentUploader';
import { PigArrivalModal } from '@/components/cycles/PigArrivalModal';
import { MortalityLogModal } from '@/components/cycles/MortalityLogModal';
import { PigTransferModal } from '@/components/cycles/PigTransferModal';
import { CycleStatusModal } from '@/components/cycles/CycleStatusModal';
import { FeedDeliveryModal } from '@/components/feed/FeedDeliveryModal';
import { BulkFeedInvoiceModal } from '@/components/feed/BulkFeedInvoiceModal';
import { FeedTransferModal } from '@/components/feed/FeedTransferModal';
import { HealthEventModal } from '@/components/health/HealthEventModal';
import { MedicineReceiptModal } from '@/components/health/MedicineReceiptModal';
import { CycleExpenseModal } from '@/components/expenses/CycleExpenseModal';
import { PigSaleModal } from '@/components/sales/PigSaleModal';
import { ScaleSlipPrintModal } from '@/components/sales/ScaleSlipPrintModal';
import { ScaleItemsDetailModal } from '@/components/sales/ScaleItemsDetailModal';
import { SettlementAdjustmentModal } from '@/components/settlements/SettlementAdjustmentModal';
import { getAccountName } from '@/lib/chartOfAccounts';
import { SoftVoidConfirmModal } from '@/components/common/SoftVoidConfirmModal';
import { formatDate } from '@/lib/utils';
import { deriveWorkflowStatus } from '@/lib/workflowStatus';
import { useAuth } from '@/context/AuthContext';
import { cycleService } from '@/services/cycleService';
import { arrivalService } from '@/services/arrivalService';
import { mortalityService } from '@/services/mortalityService';
import { weightCheckService } from '@/services/weightCheckService';
import { transferService } from '@/services/transferService';
import { feedService } from '@/services/feedService';
import { healthService } from '@/services/healthService';
import { expenseService } from '@/services/expenseService';
import { settlementService, CycleSettlementSummary } from '@/services/settlementService';
import { formatCurrency } from '@/lib/utils';
import {
  ProductionCycleRecord,
  PigArrivalRecord,
  MortalityEventRecord,
  WeightCheckRecord,
  PigTransferRecord,
  FeedReceiptRecord,
  FeedTransferRecord,
  HealthEventRecord,
  MedicineReceiptRecord,
  ExpenseRecord,
  SettlementRecord,
  ReactiveHeadcountSummary,
} from '@/types/database';

export const CycleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isOperations } = useAuth();

  const [cycle, setCycle] = useState<ProductionCycleRecord | null>(null);
  const [headcount, setHeadcount] = useState<ReactiveHeadcountSummary>({
    pigsReceived: 0,
    transfersIn: 0,
    mortality: 0,
    transfersOut: 0,
    pigsSold: 0,
    expectedCurrentHeadcount: 0,
  });

  // Operational activity states
  const [arrivals, setArrivals] = useState<PigArrivalRecord[]>([]);
  const [mortalities, setMortalities] = useState<MortalityEventRecord[]>([]);
  const [weightChecks, setWeightChecks] = useState<WeightCheckRecord[]>([]);
  const [transfersIn, setTransfersIn] = useState<PigTransferRecord[]>([]);
  const [transfersOut, setTransfersOut] = useState<PigTransferRecord[]>([]);
  const [sales, setSales] = useState<any[]>([]);

  // Feed & Expense states
  const [directFeedReceipts, setDirectFeedReceipts] = useState<FeedReceiptRecord[]>([]);
  const [feedTransfers, setFeedTransfers] = useState<FeedTransferRecord[]>([]);
  const [healthEvents, setHealthEvents] = useState<HealthEventRecord[]>([]);
  const [medicineReceipts, setMedicineReceipts] = useState<MedicineReceiptRecord[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);

  const [activeTab, setActiveTab] = useState<string>('overview');
  const [loading, setLoading] = useState<boolean>(true);

  // Modals state
  const [showArrivalModal, setShowArrivalModal] = useState<boolean>(false);
  const [showMortalityModal, setShowMortalityModal] = useState<boolean>(false);
  const [showWeightCheckModal, setShowWeightCheckModal] = useState<boolean>(false);
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false);

  // Feed Modals
  const [showDirectFeedModal, setShowDirectFeedModal] = useState<boolean>(false);
  const [showBulkFeedModal, setShowBulkFeedModal] = useState<boolean>(false);
  const [showFeedTransferModal, setShowFeedTransferModal] = useState<boolean>(false);
  const [showHealthModal, setShowHealthModal] = useState<boolean>(false);
  const [showMedicineModal, setShowMedicineModal] = useState<boolean>(false);
  const [showExpenseModal, setShowExpenseModal] = useState<boolean>(false);

  // Soft-void modal state
  const [voidModalState, setVoidModalState] = useState<{
    isOpen: boolean;
    tableName: string;
    recordId: string;
    recordIdentifier: string;
  }>({
    isOpen: false,
    tableName: '',
    recordId: '',
    recordIdentifier: '',
  });

  // Document preview state
  const [previewDoc, setPreviewDoc] = useState<any | null>(null);

  // Expandable arrival rows
  const [expandedArrivals, setExpandedArrivals] = useState<Record<string, boolean>>({});

  // WP10 Sales Modals
  const [showPigSaleModal, setShowPigSaleModal] = useState<boolean>(false);
  const [selectedSaleForPrint, setSelectedSaleForPrint] = useState<any | null>(null);
  const [selectedSaleForItems, setSelectedSaleForItems] = useState<any | null>(null);
  const [expandedSales, setExpandedSales] = useState<Record<string, boolean>>({});

  // WP11 Settlement state
  const [settlementSummary, setSettlementSummary] = useState<CycleSettlementSummary | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [
        detailRes,
        weightChecksRes,
        fullArrivalsRes,
        fullMortalityRes,
        transfersRes,
        feedDirectRes,
        feedTransfersRes,
        healthEventsRes,
        medReceiptsRes,
        expensesRes,
        settlementRes,
      ] = await Promise.all([
        cycleService.getCycleDetail(id),
        weightCheckService.getWeightChecksByCycle(id),
        arrivalService.getArrivalsByCycle(id),
        mortalityService.getMortalityByCycle(id),
        transferService.getTransfersByCycle(id),
        feedService.getFeedReceipts({ cycleId: id }),
        feedService.getFeedTransfers({ cycleId: id }),
        healthService.getHealthEvents(id),
        healthService.getMedicineReceipts(id),
        expenseService.getExpenses({ cycleId: id }),
        settlementService.getCycleSettlementSummary(id),
      ]);

      setCycle(detailRes.cycle);
      setHeadcount(detailRes.headcount);
      setArrivals(fullArrivalsRes);
      setMortalities(fullMortalityRes);
      setWeightChecks(weightChecksRes);
      setTransfersIn(transfersRes.transfersIn);
      setTransfersOut(transfersRes.transfersOut);
      setSales(detailRes.sales);

      setDirectFeedReceipts(feedDirectRes);
      setFeedTransfers(feedTransfersRes);
      setHealthEvents(healthEventsRes);
      setMedicineReceipts(medReceiptsRes);
      setExpenses(expensesRes);
      setSettlementSummary(settlementRes);
    } catch (err: any) {
      console.error('Error loading cycle details:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleArrivalExpand = (arrivalId: string) => {
    setExpandedArrivals((prev) => ({
      ...prev,
      [arrivalId]: !prev[arrivalId],
    }));
  };

  // Exact 8 Structured Tabs from docs/VSB_UI_UX_SPEC.md Section 3.3
  const tabs = [
    { id: 'overview', name: '1. Overview', icon: Layers },
    { id: 'pigs', name: '2. Pigs & Arrivals', icon: Scale, count: arrivals.length + transfersIn.length + transfersOut.length },
    { id: 'feed', name: '3. Feed', icon: Wheat, count: directFeedReceipts.length + feedTransfers.length },
    { id: 'health', name: '4. Health & Mortality', icon: Syringe, count: mortalities.length + healthEvents.length + medicineReceipts.length },
    { id: 'expenses', name: '5. Expenses', icon: Receipt, count: expenses.length },
    { id: 'sales', name: '6. Sales', icon: Tag, count: sales.length },
    { id: 'documents', name: '7. Documents', icon: FolderLock },
    { id: 'settlement', name: '8. Settlement', icon: FileSpreadsheet },
  ];

  // Calculate Days on Feed
  let daysOnFeed: number | null = null;
  if (cycle?.start_date) {
    const startDate = new Date(cycle.start_date);
    const endDate = cycle.actual_close_date ? new Date(cycle.actual_close_date) : new Date();
    const diffTime = endDate.getTime() - startDate.getTime();
    daysOnFeed = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
  }

  // --- Dynamic Feed Aggregations (Non-Void only) ---
  const totalDirectBags = directFeedReceipts.reduce((sum, r) => sum + (Number(r.quantity_bags) || 0), 0);
  const totalDirectKg = directFeedReceipts.reduce((sum, r) => sum + (Number(r.total_weight_kg) || 0), 0);
  const totalDirectCost = directFeedReceipts.reduce((sum, r) => sum + (Number(r.total_cost) || 0), 0);

  const feedTransfersIn = feedTransfers.filter((t) => t.destination_cycle_id === id);
  const feedTransfersOut = feedTransfers.filter((t) => t.source_cycle_id === id);

  const totalTransfersInBags = feedTransfersIn.reduce((sum, t) => sum + (Number(t.quantity_bags) || 0), 0);
  const totalTransfersInCost = feedTransfersIn.reduce((sum, t) => sum + (Number(t.total_transferred_cost) || 0), 0);

  const totalTransfersOutBags = feedTransfersOut.reduce((sum, t) => sum + (Number(t.quantity_bags) || 0), 0);
  const totalTransfersOutCost = feedTransfersOut.reduce((sum, t) => sum + (Number(t.total_transferred_cost) || 0), 0);

  const totalFeedBagsConsumed = totalDirectBags + totalTransfersInBags - totalTransfersOutBags;
  const totalFeedCost = totalDirectCost + totalTransfersInCost - totalTransfersOutCost;
  const totalFeedKgConsumed = totalDirectKg + (totalTransfersInBags - totalTransfersOutBags) * 50;

  // --- Strict Physical KPI Standards (Complete, Compatible Physical Cohort Only) ---
  // FCR may ONLY be calculated when actual measured physical data forms a complete, compatible cohort:
  // 1. Zero live pigs remaining on feed (headcount.expectedCurrentHeadcount === 0).
  // 2. Zero mortality loss (headcount.mortality === 0) — mortality prevents a fully measured physical denominator.
  // 3. Zero transfers in or out (headcount.transfersIn === 0 && headcount.transfersOut === 0) — transfers lack compatible intake/exit scale weights.
  // 4. Single compatible intake arrival cohort (arrivals.length === 1 && totalIntakeWeightKg > 0).
  // 5. 100% placed pigs sold with measured sale scale checkout weights (headcount.pigsSold === headcount.pigsReceived && headcount.pigsSold > 0).
  // 6. Total measured gross sales scale weight exceeds total intake arrival weight and measured feed kg > 0.
  // Biological weight gain = Gross Physical Sale Scale Weight - Intake Weight (commercial shrinkage is not deducted from biological mass).
  // If ANY of these conditions are not met, FCR strictly displays 'N/A — insufficient measured data'.
  const totalIntakeWeightKg = arrivals.reduce((sum, a) => sum + (Number(a.total_weight_kg) || 0), 0);
  const totalGrossSalesWeightKg = sales.reduce((sum, s) => sum + (Number(s.gross_weight_kg) || 0), 0);
  const totalNetSalesWeightKg = sales.reduce((sum, s) => sum + (Number(s.net_weight_kg) || 0), 0);
  const totalShrinkageSalesKg = sales.reduce((sum, s) => sum + (Number(s.shrinkage_deduction_kg) || 0), 0);
  const totalSalesRevenue = sales.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);

  const isCohortCompleteAndCompatible =
    headcount.expectedCurrentHeadcount === 0 &&
    headcount.mortality === 0 &&
    headcount.transfersIn === 0 &&
    headcount.transfersOut === 0 &&
    headcount.pigsReceived > 0 &&
    headcount.pigsSold === headcount.pigsReceived &&
    arrivals.length === 1 &&
    totalIntakeWeightKg > 0 &&
    totalFeedKgConsumed > 0 &&
    totalGrossSalesWeightKg > totalIntakeWeightKg;

  let verifiedFcr: number | null = null;
  let weightGainKg: number | null = null;

  if (isCohortCompleteAndCompatible) {
    const computedGain = totalGrossSalesWeightKg - totalIntakeWeightKg;
    weightGainKg = computedGain;
    verifiedFcr = totalFeedKgConsumed / computedGain;
  }

  // --- Expenses & Health Totals ---
  const totalBookedExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const totalHealthCost =
    healthEvents.reduce((sum, h) => sum + (Number(h.total_cost) || 0), 0) +
    medicineReceipts.reduce((sum, m) => sum + (Number(m.total_amount) || 0), 0);

  const handleSoftVoidConfirm = async (reason: string) => {
    await cycleService.softVoidRecord(voidModalState.tableName, voidModalState.recordId, reason);
    setVoidModalState({ isOpen: false, tableName: '', recordId: '', recordIdentifier: '' });
    loadData();
  };

  // Derived user-facing canonical workflow status: Active -> Draft -> Finalized -> Closed
  const derivedDetailStatus = useMemo(() => {
    if (!cycle) return 'active';
    return deriveWorkflowStatus({
      cycleStatus: cycle.status,
      settlement: settlementSummary?.settlement
        ? {
            ...settlementSummary.settlement,
            isFrozen: settlementSummary.isFrozen,
          }
        : undefined,
    });
  }, [cycle, settlementSummary]);

  if (loading) {
    return <div className="p-12 text-center text-slate-400 text-sm">Loading cycle details...</div>;
  }

  if (!cycle) {
    return (
      <div className="p-12 text-center">
        <p className="text-slate-700 font-medium">Production Cycle not found.</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/cycles')} className="mt-3">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Cycles
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Top Navigation & Quick Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <button
          onClick={() => navigate('/cycles')}
          className="inline-flex items-center text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Production Cycles
        </button>

        {isOperations && (
          <div className="flex flex-wrap items-center gap-2">
            <ActionTooltip content="Record new piglet intake batch, head count, initial weights, and supplier.">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowArrivalModal(true)}
                className="text-xs h-8 gap-1 text-emerald-800 border-emerald-300 hover:bg-emerald-50"
              >
                <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-700" /> Log Arrival
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Log mortality incident with date, cause of death, and disposal notes.">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowMortalityModal(true)}
                className="text-xs h-8 gap-1 text-red-800 border-red-300 hover:bg-red-50"
              >
                <Skull className="w-3.5 h-3.5 text-red-600" /> Log Mortality
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Log bulk feed delivery invoice allocated directly to this production cycle.">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkFeedModal(true)}
                className="text-xs h-8 gap-1 text-amber-800 border-amber-300 hover:bg-amber-50"
              >
                <Wheat className="w-3.5 h-3.5 text-amber-700" /> + Feed Invoice
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Record veterinary treatment, vaccines, medications, or biosecurity monitoring event.">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowHealthModal(true)}
                className="text-xs h-8 gap-1 text-teal-800 border-teal-300 hover:bg-teal-50"
              >
                <Syringe className="w-3.5 h-3.5 text-teal-700" /> Health / ASF
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Record operational, labor, hauling, electricity, or utility expense for this cycle.">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowExpenseModal(true)}
                className="text-xs h-8 gap-1 text-slate-800 border-slate-300 hover:bg-slate-50"
              >
                <Receipt className="w-3.5 h-3.5 text-slate-700" /> Log Expense
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Record commercial sales batch, head count, live chute weight, and agreed buyer price.">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPigSaleModal(true)}
                className="text-xs h-8 gap-1 text-emerald-900 border-emerald-400 bg-emerald-50/50 hover:bg-emerald-100 font-semibold"
              >
                <Tag className="w-3.5 h-3.5 text-emerald-700" /> Log Pig Sale
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Update cycle lifecycle state (Active, Settled, or Closed).">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowStatusModal(true)}
                className="text-xs h-8 gap-1 text-slate-800 border-slate-300"
              >
                <Edit className="w-3.5 h-3.5 text-slate-500" /> Status
              </Button>
            </ActionTooltip>
          </div>
        )}
      </div>

      {/* Cycle Header & Reactive Live Headcount Banner */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold font-mono text-slate-900">{cycle.cycle_code}</h1>
              <StatusBadge status={derivedDetailStatus} category="cycle" />
            </div>
            <p className="text-xs sm:text-sm text-slate-600 mt-1 font-medium">
              Grower:{' '}
              <strong className="text-slate-900">
                {cycle.growers?.full_name || (cycle.operation_type === 'company_owned' ? 'Company-Owned' : 'Unassigned')}
              </strong>{' '}
              • Farm: <strong className="text-slate-900">{cycle.farms?.farm_name || 'Unassigned'}</strong>
              {cycle.production_units?.unit_code ? ` / Unit ${cycle.production_units.unit_code}` : ''}
              {cycle.grower_contracts && (
                <span className="ml-2 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-mono">
                  Split: {cycle.grower_contracts.vsb_share_pct}% VSB / {cycle.grower_contracts.grower_share_pct}% Grower
                </span>
              )}
            </p>
          </div>
          <div className="text-left sm:text-right text-xs text-slate-500 font-mono space-y-0.5">
            <p>Placement: {formatDate(cycle.start_date)}</p>
            {cycle.expected_sale_date && <p>Expected Sale: {formatDate(cycle.expected_sale_date)}</p>}
            {cycle.actual_close_date && <p>Closed: {formatDate(cycle.actual_close_date)}</p>}
          </div>
        </div>

        {/* Reactive Headcount Balance Callout Bar */}
        <div className="bg-slate-900 text-white rounded-lg p-3 sm:p-4 text-xs font-mono space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-2">
            <span className="text-[11px] uppercase font-bold text-emerald-400 tracking-wider">
              Reactive Live Headcount Engine
            </span>
            <span className="text-slate-400 text-[11px]">
              Days on Feed: <strong className="text-white">{daysOnFeed !== null ? `${daysOnFeed} days` : 'N/A'}</strong>
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-300">
            <span>
              Received: <strong className="text-emerald-400 font-bold">+{headcount.pigsReceived}</strong>
            </span>
            <span>
              Transfers In: <strong className="text-purple-400 font-bold">+{headcount.transfersIn}</strong>
            </span>
            <span>
              Mortality: <strong className="text-red-400 font-bold">-{headcount.mortality}</strong>
            </span>
            <span>
              Transfers Out: <strong className="text-purple-400 font-bold">-{headcount.transfersOut}</strong>
            </span>
            <span>
              Sold: <strong className="text-amber-400 font-bold">-{headcount.pigsSold}</strong>
            </span>
          </div>

          <div className="pt-1 flex items-center justify-between border-t border-slate-800">
            <span className="text-xs text-slate-300">Remaining Balance:</span>
            <span className="text-base sm:text-lg font-bold text-emerald-400 font-mono">
              {headcount.expectedCurrentHeadcount}
            </span>
          </div>
        </div>
      </div>

      {/* Exactly 8 Structured Tabs */}
      <div className="border-b border-slate-200 overflow-x-auto no-scrollbar">
        <nav className="flex space-x-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2.5 px-3.5 border-b-2 text-xs font-bold whitespace-nowrap transition-colors ${
                  isActive
                    ? 'border-emerald-700 text-emerald-800 bg-emerald-50/50 rounded-t-md'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.name}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="px-1.5 py-0.2 rounded-full bg-slate-200 text-slate-700 text-[10px] font-mono">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content Body */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
        {/* Tab 1: Overview */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Pigs Delivered</span>
                <p className="text-2xl font-bold font-mono text-slate-900 mt-1">{headcount.pigsReceived}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {headcount.pigsSold > 0
                    ? `${headcount.pigsSold} sold • ${headcount.mortality} mortality`
                    : `${headcount.mortality} mortality`}
                </p>
              </div>

              <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Feed Consumed</span>
                <p className="text-2xl font-bold font-mono text-slate-900 mt-1">
                  {totalFeedBagsConsumed.toLocaleString()} <span className="text-xs font-sans text-slate-500">bags</span>
                </p>
                <p className="text-xs font-mono text-slate-500 mt-0.5">
                  Cost: ₱{totalFeedCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>

              <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Live Pigs on Feed</span>
                <p className="text-2xl font-bold font-mono text-slate-900 mt-1">
                  {headcount.expectedCurrentHeadcount}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {cycle.status === 'settled' || cycle.status === 'closed' ? 'Batch liquidated' : 'Current active head'}
                </p>
              </div>

              <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Booked Expenses</span>
                <p className="text-2xl font-bold font-mono text-slate-900 mt-1">
                  ₱{totalBookedExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{expenses.length} itemized lines</p>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4 space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Cycle Metadata & Lineage</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-600 font-mono">
                <div>
                  <span className="text-slate-400">Operation Type:</span>{' '}
                  <strong className="text-slate-800">{cycle.operation_type}</strong>
                </div>
                <div>
                  <span className="text-slate-400">Batch Code:</span>{' '}
                  <strong className="text-slate-800">{cycle.batch_code}</strong>
                </div>
                <div>
                  <span className="text-slate-400">Legacy Raiser:</span>{' '}
                  <strong className="text-slate-800">{cycle.legacy_raiser_name || '—'}</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Pigs & Arrivals (From WP08) */}
        {activeTab === 'pigs' && (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Pig Intake & Placement Events</h3>
                  <p className="text-xs text-slate-500">
                    Batches placed into this cycle with optional individual scale weighing sheets.
                  </p>
                </div>
                {isOperations && (
                  <Button
                    size="sm"
                    onClick={() => setShowArrivalModal(true)}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs h-8 font-semibold"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Log Pig Arrival
                  </Button>
                )}
              </div>

              {arrivals.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-slate-200 rounded-lg">
                  <p className="text-xs text-slate-500">No pig arrival events logged yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {arrivals.map((arr) => {
                    const isExpanded = !!expandedArrivals[arr.id];
                    const items = arr.pig_arrival_items || [];
                    const avgWeight =
                      arr.head_count > 0 && arr.total_weight_kg
                        ? (Number(arr.total_weight_kg) / arr.head_count).toFixed(2)
                        : '—';

                    return (
                      <div key={arr.id} className="border border-slate-200 rounded-lg overflow-hidden">
                        <div className="bg-slate-50 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleArrivalExpand(arr.id)}
                              className="p-1 hover:bg-slate-200 rounded text-slate-600 transition-colors"
                            >
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            <div>
                              <p className="text-xs font-mono font-bold text-slate-900">
                                {arr.arrival_number || 'Arrival Event'} • {formatDate(arr.arrival_date)}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                Supplier: <strong className="text-slate-700">{arr.suppliers?.supplier_name || 'Unassigned'}</strong>
                                {arr.delivery_receipt_no ? ` • DR: ${arr.delivery_receipt_no}` : ''}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 text-xs font-mono">
                            <div className="text-right">
                              <span className="text-emerald-700 font-bold">+{arr.head_count} head</span>
                              <span className="text-slate-400 mx-1">•</span>
                              <span className="text-slate-700 font-bold">{arr.total_weight_kg ? `${arr.total_weight_kg} kg` : 'N/A'}</span>
                              <span className="text-slate-400 mx-1">•</span>
                              <span className="text-slate-500">Avg: {avgWeight} kg</span>
                            </div>
                            {arr.documents && arr.documents.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setPreviewDoc(arr.documents![0])}
                                className="h-7 px-2 text-xs text-slate-600"
                                title="View Attached Paper Slip"
                              >
                                <Paperclip className="w-3.5 h-3.5 text-emerald-700" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="p-3 border-t border-slate-200 bg-white">
                            {items.length > 0 ? (
                              <div className="space-y-2">
                                <h5 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                                  Individual Paper Scale Weights ({items.length} records)
                                </h5>
                                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded">
                                  <table className="w-full text-xs text-left font-mono">
                                    <thead className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                                      <tr>
                                        <th className="py-1 px-3 text-center">#</th>
                                        <th className="py-1 px-3 text-right">Weight (kg)</th>
                                        <th className="py-1 px-3">Ear Tag / ID</th>
                                        <th className="py-1 px-3">Notes</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {items.map((item) => (
                                        <tr key={item.id} className="hover:bg-slate-50">
                                          <td className="py-1 px-3 text-center text-slate-500">{item.sequence_no}</td>
                                          <td className="py-1 px-3 text-right font-bold text-slate-900">{Number(item.weight_kg).toFixed(2)}</td>
                                          <td className="py-1 px-3 text-slate-700 font-sans">{item.tag_no || '—'}</td>
                                          <td className="py-1 px-3 text-slate-600 font-sans">{item.notes || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-slate-400 italic">
                                No individual pig weights logged. Total batch intake weight was entered in bulk.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Transfers In & Out Section */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Inter-Cycle & Inter-Pen Transfers</h4>
                  <p className="text-xs text-slate-500">Transfers moved into or out of this production cycle.</p>
                </div>
                {isOperations && (
                  <Button
                    size="sm"
                    onClick={() => setShowTransferModal(true)}
                    className="bg-purple-700 hover:bg-purple-800 text-white text-xs h-8 font-semibold"
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Log Pig Transfer
                  </Button>
                )}
              </div>

              {transfersIn.length === 0 && transfersOut.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-slate-200 rounded-lg">
                  <p className="text-xs text-slate-500">No transfers recorded for this cycle.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transfersIn.length > 0 && (
                    <div>
                      <h5 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">
                        Transfers In (+{headcount.transfersIn} head)
                      </h5>
                      <div className="border border-slate-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                            <tr>
                              <th className="py-2 px-3">Date</th>
                              <th className="py-2 px-3">Source Cycle</th>
                              <th className="py-2 px-3 text-right">Head Count</th>
                              <th className="py-2 px-3">Reason</th>
                              <th className="py-2 px-3">Notes</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {transfersIn.map((t) => (
                              <tr key={t.id} className="hover:bg-slate-50">
                                <td className="py-1.5 px-3 font-mono">{formatDate(t.transfer_date)}</td>
                                <td className="py-1.5 px-3 font-mono font-bold text-slate-800">
                                  {t.source_cycle?.cycle_code || 'External'}
                                </td>
                                <td className="py-1.5 px-3 text-right font-mono font-bold text-emerald-700">
                                  +{t.head_count} head
                                </td>
                                <td className="py-1.5 px-3 text-slate-700">{t.reason}</td>
                                <td className="py-1.5 px-3 text-slate-500">{t.notes || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {transfersOut.length > 0 && (
                    <div>
                      <h5 className="text-xs font-bold text-purple-800 uppercase tracking-wider mb-2">
                        Transfers Out (-{headcount.transfersOut} head)
                      </h5>
                      <div className="border border-slate-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                            <tr>
                              <th className="py-2 px-3">Date</th>
                              <th className="py-2 px-3">Destination Cycle</th>
                              <th className="py-2 px-3 text-right">Head Count</th>
                              <th className="py-2 px-3">Reason</th>
                              <th className="py-2 px-3">Notes</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {transfersOut.map((t) => (
                              <tr key={t.id} className="hover:bg-slate-50">
                                <td className="py-1.5 px-3 font-mono">{formatDate(t.transfer_date)}</td>
                                <td className="py-1.5 px-3 font-mono font-bold text-slate-800">
                                  {t.destination_cycle?.cycle_code || 'External'}
                                </td>
                                <td className="py-1.5 px-3 text-right font-mono font-bold text-purple-700">
                                  -{t.head_count} head
                                </td>
                                <td className="py-1.5 px-3 text-slate-700">{t.reason}</td>
                                <td className="py-1.5 px-3 text-slate-500">{t.notes || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 3: Feed (Direct Deliveries & Cycle Transfers) */}
        {activeTab === 'feed' && (
          <div className="space-y-6">
            {/* Feed KPI Summary Callouts */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="p-3.5 rounded-lg border border-slate-200 bg-amber-50/50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">Direct Deliveries</span>
                <p className="text-xl font-bold font-mono text-slate-900 mt-1">
                  {totalDirectBags.toLocaleString()} <span className="text-xs font-sans text-slate-500">bags</span>
                </p>
                <p className="text-xs text-slate-600 font-mono mt-0.5">
                  ₱{totalDirectCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>

              <div className="p-3.5 rounded-lg border border-slate-200 bg-purple-50/50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-purple-900">Transfers In (Debits)</span>
                <p className="text-xl font-bold font-mono text-purple-900 mt-1">
                  +{totalTransfersInBags.toLocaleString()} <span className="text-xs font-sans text-slate-500">bags</span>
                </p>
                <p className="text-xs text-purple-700 font-mono mt-0.5">
                  +₱{totalTransfersInCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>

              <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Transfers Out (Credits)</span>
                <p className="text-xl font-bold font-mono text-slate-700 mt-1">
                  -{totalTransfersOutBags.toLocaleString()} <span className="text-xs font-sans text-slate-500">bags</span>
                </p>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  -₱{totalTransfersOutCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>

              <div className="p-3.5 rounded-lg border border-emerald-300 bg-emerald-50/60">
                <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-900">Net Feed Allocated</span>
                <p className="text-xl font-bold font-mono text-emerald-950 mt-1">
                  {totalFeedBagsConsumed.toLocaleString()} <span className="text-xs font-sans text-emerald-700">bags</span>
                </p>
                <p className="text-xs text-emerald-800 font-mono font-bold mt-0.5">
                  ₱{totalFeedCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            {/* Section A: Direct-to-Cycle Deliveries */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">1. Direct-to-Cycle Deliveries & Invoices</h4>
                  <p className="text-xs text-slate-500">Supplier feed deliveries allocated directly to this production cycle.</p>
                </div>
                {isOperations && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => setShowBulkFeedModal(true)}
                      className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8 font-semibold gap-1"
                    >
                      <Layers className="w-3.5 h-3.5 text-emerald-400" /> + Bulk Invoice
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowDirectFeedModal(true)}
                      className="text-xs h-8 border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      <Truck className="w-3.5 h-3.5 mr-1" /> + Single Delivery
                    </Button>
                  </div>
                )}
              </div>

              {directFeedReceipts.length === 0 ? (
                <div className="p-4 text-center border border-dashed border-slate-200 rounded-lg">
                  <p className="text-xs text-slate-500">No direct supplier deliveries logged for this cycle.</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs text-left font-sans">
                    <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3">Date</th>
                        <th className="py-2.5 px-3">Invoice / DR #</th>
                        <th className="py-2.5 px-3">Supplier</th>
                        <th className="py-2.5 px-3">Feed Product</th>
                        <th className="py-2.5 px-3 text-right">Bags</th>
                        <th className="py-2.5 px-3 text-right">Price/bag</th>
                        <th className="py-2.5 px-3 text-right">Markup / Trucking</th>
                        <th className="py-2.5 px-3 text-right">Total Cost</th>
                        <th className="py-2.5 px-3 text-center">Slip</th>
                        {isOperations && <th className="py-2.5 px-3 text-center">Action</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {directFeedReceipts.map((r) => {
                        const markupTrucking = (Number(r.feed_markup_amount) || 0) + (Number(r.trucking_cost) || 0);
                        const hasDoc = r.documents && r.documents.length > 0;

                        return (
                          <tr key={r.id} className="hover:bg-slate-50">
                            <td className="py-2 px-3 font-mono text-slate-800">{formatDate(r.receipt_date)}</td>
                            <td className="py-2 px-3 font-mono font-medium text-slate-900">
                              {r.invoice_no ? `Inv #${r.invoice_no}` : r.delivery_receipt_no ? `DR #${r.delivery_receipt_no}` : (r.receipt_number || '—')}
                            </td>
                            <td className="py-2 px-3 text-slate-700">{r.suppliers?.supplier_name || '—'}</td>
                            <td className="py-2 px-3 font-medium text-slate-900">{r.feed_products?.product_name || '—'}</td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                              {Number(r.quantity_bags).toLocaleString()}
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-slate-600">₱{Number(r.unit_price).toFixed(2)}</td>
                            <td className="py-2 px-3 text-right font-mono text-slate-600">
                              {markupTrucking > 0 ? `₱${markupTrucking.toFixed(2)}` : '—'}
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                              ₱{Number(r.total_cost).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {hasDoc ? (
                                <button
                                  onClick={() => setPreviewDoc(r.documents![0])}
                                  className="p-1 text-emerald-700 hover:text-emerald-900"
                                  title="View Invoice Document"
                                >
                                  <Paperclip className="w-4 h-4" />
                                </button>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            {isOperations && (
                              <td className="py-2 px-3 text-center">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setVoidModalState({
                                      isOpen: true,
                                      tableName: 'feed_receipts',
                                      recordId: r.id,
                                      recordIdentifier: r.receipt_number || r.id,
                                    })
                                  }
                                  className="h-6 px-1.5 text-xs text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section B: Cycle-to-Cycle Feed Transfers */}
            <div className="space-y-3 pt-2 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">2. Cycle-to-Cycle Feed Transfers</h4>
                  <p className="text-xs text-slate-500">
                    Transfers moved between cycles with exact proportional cost preservation.
                  </p>
                </div>
                {isOperations && (
                  <Button
                    size="sm"
                    onClick={() => setShowFeedTransferModal(true)}
                    className="bg-purple-700 hover:bg-purple-800 text-white text-xs h-8 font-semibold gap-1"
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> + Transfer Feed
                  </Button>
                )}
              </div>

              {feedTransfers.length === 0 ? (
                <div className="p-4 text-center border border-dashed border-slate-200 rounded-lg">
                  <p className="text-xs text-slate-500">No feed transfers recorded for this cycle.</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs text-left font-sans">
                    <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3">Date</th>
                        <th className="py-2.5 px-3">Transfer Type</th>
                        <th className="py-2.5 px-3">Counterparty Cycle</th>
                        <th className="py-2.5 px-3">Feed Product</th>
                        <th className="py-2.5 px-3 text-right">Bags</th>
                        <th className="py-2.5 px-3 text-right">Unit Transferred Cost</th>
                        <th className="py-2.5 px-3 text-right">Transferred Amount</th>
                        <th className="py-2.5 px-3">Source Lineage</th>
                        <th className="py-2.5 px-3">Notes</th>
                        {isOperations && <th className="py-2.5 px-3 text-center">Action</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {feedTransfers.map((t) => {
                        const isInbound = t.destination_cycle_id === id;
                        const counterparty = isInbound ? t.source_cycle : t.destination_cycle;

                        return (
                          <tr key={t.id} className="hover:bg-slate-50">
                            <td className="py-2 px-3 font-mono text-slate-800">{formatDate(t.transfer_date)}</td>
                            <td className="py-2 px-3 font-semibold">
                              {isInbound ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono bg-purple-100 text-purple-800 border border-purple-200">
                                  Inbound (Debit)
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono bg-slate-100 text-slate-700 border border-slate-200">
                                  Outbound (Credit)
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 font-mono text-slate-900 font-medium">
                              {counterparty?.cycle_code || 'Counterparty Cycle'}
                            </td>
                            <td className="py-2 px-3 font-medium text-slate-900">{t.feed_products?.product_name || '—'}</td>
                            <td className="py-2 px-3 text-right font-mono font-bold">
                              {isInbound ? (
                                <span className="text-purple-800">+{Number(t.quantity_bags).toLocaleString()}</span>
                              ) : (
                                <span className="text-slate-600">-{Number(t.quantity_bags).toLocaleString()}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-slate-600">
                              ₱{Number(t.unit_price).toFixed(2)}
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold">
                              {isInbound ? (
                                <span className="text-purple-800">+₱{Number(t.total_transferred_cost).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                              ) : (
                                <span className="text-slate-700">-₱{Number(t.total_transferred_cost).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 font-mono text-[11px] text-slate-500">
                              {t.source_feed_receipt ? `Receipt #${t.source_feed_receipt.receipt_number}` : 'Weighted Avg'}
                            </td>
                            <td className="py-2 px-3 text-slate-500 max-w-xs truncate">{t.notes || '—'}</td>
                            {isOperations && (
                              <td className="py-2 px-3 text-center">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setVoidModalState({
                                      isOpen: true,
                                      tableName: 'feed_transfers',
                                      recordId: t.id,
                                      recordIdentifier: `Transfer of ${t.quantity_bags} bags`,
                                    })
                                  }
                                  className="h-6 px-1.5 text-xs text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 4: Health & Mortality (WP09 Treatments, ASF Diagnostics, Medicine Receipts) */}
        {activeTab === 'health' && (
          <div className="space-y-6">
            {/* Section 1: Mortality Events (WP08) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">1. Swine Mortality Records</h3>
                  <p className="text-xs text-slate-500">Animal mortality events and causes of death.</p>
                </div>
                {isOperations && (
                  <Button
                    size="sm"
                    onClick={() => setShowMortalityModal(true)}
                    className="bg-red-600 hover:bg-red-700 text-white text-xs h-7 font-semibold"
                  >
                    <Skull className="w-3.5 h-3.5 mr-1" /> Log Mortality
                  </Button>
                )}
              </div>

              {mortalities.length === 0 ? (
                <div className="p-4 text-center border border-dashed border-slate-200 rounded-lg">
                  <p className="text-xs text-slate-500">Zero mortality events recorded for this cycle.</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="py-2 px-3">Date</th>
                        <th className="py-2 px-3 text-right">Head Lost</th>
                        <th className="py-2 px-3">Cause of Death</th>
                        <th className="py-2 px-3">Disposal Method</th>
                        <th className="py-2 px-3">Pen</th>
                        <th className="py-2 px-3">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {mortalities.map((m) => (
                        <tr key={m.id} className="hover:bg-slate-50">
                          <td className="py-1.5 px-3 font-mono font-medium text-slate-800">{formatDate(m.event_date)}</td>
                          <td className="py-1.5 px-3 text-right font-mono font-bold text-red-700">-{m.head_count} head</td>
                          <td className="py-1.5 px-3 font-medium text-slate-800">{m.cause_of_death || 'Unspecified'}</td>
                          <td className="py-1.5 px-3 text-slate-600">{m.disposal_method || '—'}</td>
                          <td className="py-1.5 px-3 text-slate-600">{m.production_units?.unit_name || '—'}</td>
                          <td className="py-1.5 px-3 text-slate-500">{m.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 2: Health & ASF Diagnostic Events (WP09) */}
            <div className="space-y-3 pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">2. Treatments & ASF Diagnostic Events</h4>
                  <p className="text-xs text-slate-500">ASF blood tests, laboratory findings, and veterinary treatments.</p>
                </div>
                {isOperations && (
                  <Button
                    size="sm"
                    onClick={() => setShowHealthModal(true)}
                    className="bg-teal-700 hover:bg-teal-800 text-white text-xs h-7 font-semibold"
                  >
                    <Syringe className="w-3.5 h-3.5 mr-1" /> + Log Health / ASF Event
                  </Button>
                )}
              </div>

              {healthEvents.length === 0 ? (
                <div className="p-4 text-center border border-dashed border-slate-200 rounded-lg">
                  <p className="text-xs text-slate-500">No medical treatments or ASF diagnostic events logged.</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="py-2 px-3">Date</th>
                        <th className="py-2 px-3">Event Classification</th>
                        <th className="py-2 px-3">Diagnostic Result / ASF</th>
                        <th className="py-2 px-3">Medicine Used</th>
                        <th className="py-2 px-3 text-right">Head Treated</th>
                        <th className="py-2 px-3 text-right">Cost (₱)</th>
                        <th className="py-2 px-3">Attending Vet</th>
                        <th className="py-2 px-3">Notes</th>
                        <th className="py-2 px-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {healthEvents.map((h) => (
                        <tr key={h.id} className="hover:bg-slate-50">
                          <td className="py-2 px-3 font-mono text-slate-800">{formatDate(h.event_date)}</td>
                          <td className="py-2 px-3 font-medium text-slate-900">{h.event_type}</td>
                          <td className="py-2 px-3 font-medium">
                            {h.diagnostic_result ? (
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  h.diagnostic_result.toLowerCase().includes('negative')
                                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                    : h.diagnostic_result.toLowerCase().includes('positive')
                                    ? 'bg-red-100 text-red-800 border border-red-300'
                                    : 'bg-amber-100 text-amber-800 border border-amber-300'
                                }`}
                              >
                                {h.diagnostic_result}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-slate-700">{h.medicine_products?.product_name || '—'}</td>
                          <td className="py-2 px-3 text-right font-mono text-slate-700">
                            {h.head_count_treated ? `${h.head_count_treated} hd` : '—'}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                            ₱{Number(h.total_cost).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 px-3 text-slate-600">{h.veterinarian_technician || '—'}</td>
                          <td className="py-2 px-3 text-slate-500">{h.notes || '—'}</td>
                          <td className="py-2 px-3 text-right space-x-1 whitespace-nowrap">
                            {h.documents && h.documents.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setPreviewDoc(h.documents![0])}
                                className="h-6 px-1.5 text-xs text-slate-600 hover:text-slate-900"
                                title="View Lab Certificate"
                              >
                                <Paperclip className="w-3.5 h-3.5 text-teal-700" />
                              </Button>
                            )}
                            {isOperations && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setVoidModalState({
                                    isOpen: true,
                                    tableName: 'health_events',
                                    recordId: h.id,
                                    recordIdentifier: h.event_number || h.id,
                                  })
                                }
                                className="h-6 px-1.5 text-xs text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 3: Veterinary Medicine Receipts (WP09) */}
            <div className="space-y-3 pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">3. Veterinary Medicine Receipts</h4>
                  <p className="text-xs text-slate-500">Purchases of pharmaceuticals and vaccines (Chart of Accounts 3030–3032).</p>
                </div>
                {isOperations && (
                  <Button
                    size="sm"
                    onClick={() => setShowMedicineModal(true)}
                    className="bg-teal-700 hover:bg-teal-800 text-white text-xs h-7 font-semibold"
                  >
                    <Pill className="w-3.5 h-3.5 mr-1" /> + Record Medicine Receipt
                  </Button>
                )}
              </div>

              {medicineReceipts.length === 0 ? (
                <div className="p-4 text-center border border-dashed border-slate-200 rounded-lg">
                  <p className="text-xs text-slate-500">No medicine purchase receipts recorded for this cycle.</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="py-2 px-3">Date</th>
                        <th className="py-2 px-3">Receipt #</th>
                        <th className="py-2 px-3">Supplier</th>
                        <th className="py-2 px-3">Product</th>
                        <th className="py-2 px-3 text-right">Quantity</th>
                        <th className="py-2 px-3 text-right">Unit Price</th>
                        <th className="py-2 px-3 text-right">Total Amount</th>
                        <th className="py-2 px-3">Invoice #</th>
                        <th className="py-2 px-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {medicineReceipts.map((m) => (
                        <tr key={m.id} className="hover:bg-slate-50">
                          <td className="py-2 px-3 font-mono text-slate-800">{formatDate(m.receipt_date)}</td>
                          <td className="py-2 px-3 font-mono font-bold text-slate-900">{m.receipt_number || '—'}</td>
                          <td className="py-2 px-3 text-slate-700">{m.suppliers?.supplier_name || '—'}</td>
                          <td className="py-2 px-3 font-medium text-slate-900">{m.medicine_products?.product_name || '—'}</td>
                          <td className="py-2 px-3 text-right font-mono text-slate-700">
                            {m.quantity} {m.unit_measurement}
                          </td>
                          <td className="py-2 px-3 text-right font-mono text-slate-600">₱{Number(m.unit_price).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                            ₱{Number(m.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 px-3 font-mono text-slate-500">{m.invoice_no || '—'}</td>
                          <td className="py-2 px-3 text-right space-x-1 whitespace-nowrap">
                            {m.documents && m.documents.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setPreviewDoc(m.documents![0])}
                                className="h-6 px-1.5 text-xs text-slate-600 hover:text-slate-900"
                              >
                                <Paperclip className="w-3.5 h-3.5 text-teal-700" />
                              </Button>
                            )}
                            {isOperations && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setVoidModalState({
                                    isOpen: true,
                                    tableName: 'medicine_receipts',
                                    recordId: m.id,
                                    recordIdentifier: m.receipt_number || m.id,
                                  })
                                }
                                className="h-6 px-1.5 text-xs text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 5: Expenses (WP09 Itemized COA Expenses) */}
        {activeTab === 'expenses' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-bold text-slate-900">Operating Expenses & Direct Debits</h3>
                <p className="text-xs text-slate-500">
                  Itemized cycle charges linked to Master Chart of Accounts (COA).
                </p>
              </div>
              {isOperations && (
                <Button
                  size="sm"
                  onClick={() => setShowExpenseModal(true)}
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8 font-semibold"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Log Operating Expense
                </Button>
              )}
            </div>

            {/* Total Expense Callout */}
            <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50/50 flex items-center justify-between font-mono">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Total Booked Cycle Expenses
                </span>
                <p className="text-xs text-slate-500 mt-0.5">{expenses.length} recorded expense transactions</p>
              </div>
              <div className="text-right">
                <span className="text-xl sm:text-2xl font-bold text-slate-900">
                  ₱{totalBookedExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {expenses.length === 0 ? (
              <div className="p-6 text-center border border-dashed border-slate-200 rounded-lg">
                <p className="text-xs text-slate-500">No itemized operating expenses recorded for this cycle.</p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3">Date</th>
                      <th className="py-2.5 px-3">Expense #</th>
                      <th className="py-2.5 px-3">COA Category</th>
                      <th className="py-2.5 px-3">Description</th>
                      <th className="py-2.5 px-3">Payee / Supplier</th>
                      <th className="py-2.5 px-3 text-right">Amount (₱)</th>
                      <th className="py-2.5 px-3">Reference #</th>
                      <th className="py-2.5 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {expenses.map((exp) => (
                      <tr key={exp.id} className="hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono text-slate-800">{formatDate(exp.expense_date)}</td>
                        <td className="py-2 px-3 font-mono font-bold text-slate-900">{exp.expense_number || '—'}</td>
                        <td className="py-2 px-3">
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-slate-100 text-slate-800 border border-slate-300">
                            [{exp.account_code || exp.transaction_categories?.code || '3050'}] {getAccountName(exp.account_code || exp.transaction_categories?.code)}
                          </span>
                        </td>
                        <td className="py-2 px-3 font-medium text-slate-900">{exp.description}</td>
                        <td className="py-2 px-3 text-slate-700">{exp.suppliers?.supplier_name || '—'}</td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                          ₱{Number(exp.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2 px-3 font-mono text-slate-500">{exp.reference_no || '—'}</td>
                        <td className="py-2 px-3 text-right space-x-1 whitespace-nowrap">
                          {exp.documents && exp.documents.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPreviewDoc(exp.documents![0])}
                              className="h-6 px-1.5 text-xs text-slate-600 hover:text-slate-900"
                              title="View Attached Receipt"
                            >
                              <Paperclip className="w-3.5 h-3.5 text-emerald-700" />
                            </Button>
                          )}
                          {isOperations && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setVoidModalState({
                                  isOpen: true,
                                  tableName: 'expenses',
                                  recordId: exp.id,
                                  recordIdentifier: exp.expense_number || exp.id,
                                })
                              }
                              className="h-6 px-1.5 text-xs text-red-600 hover:bg-red-50"
                              title="Soft-Void Expense"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab 6: Sales (Complete WP10 Implementation) */}
        {activeTab === 'sales' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">Pig Sales & Scale Tickets</h3>
                <p className="text-xs text-slate-500">
                  Granular scale weights, dynamic rate-rule shrinkage, buyer gate passes, and revenue checkout.
                </p>
              </div>
              {isOperations && (
                <Button
                  size="sm"
                  onClick={() => setShowPigSaleModal(true)}
                  className="bg-emerald-700 hover:bg-emerald-600 font-semibold gap-1.5 text-xs text-white"
                >
                  <Plus className="w-4 h-4" /> Log Pig Sale / Scale Lift
                </Button>
              )}
            </div>

            {/* Cycle Sales KPI Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card className="p-3.5 bg-slate-50 border-slate-200">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Total Lifts</span>
                <span className="text-xl font-bold font-mono text-slate-900 mt-1 block">{sales.length}</span>
                <p className="text-[10px] text-slate-500 mt-0.5">Separate sales batches</p>
              </Card>

              <Card className="p-3.5 bg-slate-50 border-slate-200">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Total Pigs Sold</span>
                <span className="text-xl font-bold font-mono text-emerald-700 mt-1 block">
                  <TabularNumber value={headcount.pigsSold} decimals={0} /> hd
                </span>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {headcount.expectedCurrentHeadcount} live head remaining
                </p>
              </Card>

              <Card className="p-3.5 bg-slate-50 border-slate-200">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Total Net Weight</span>
                <span className="text-xl font-bold font-mono text-slate-900 mt-1 block">
                  <TabularNumber value={totalNetSalesWeightKg} decimals={2} /> kg
                </span>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Gross: <span className="font-mono">{totalGrossSalesWeightKg.toFixed(1)} kg</span> • Shrink:{' '}
                  <span className="font-mono">-{totalShrinkageSalesKg.toFixed(1)} kg</span>
                </p>
              </Card>

              <Card className="p-3.5 bg-slate-50 border-slate-200">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Total Revenue</span>
                <span className="text-xl font-bold font-mono text-emerald-700 mt-1 block">
                  <TabularNumber value={totalSalesRevenue} type="currency" decimals={2} />
                </span>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Avg Rate:{' '}
                  <span className="font-mono font-medium">
                    ₱{totalNetSalesWeightKg > 0 ? (totalSalesRevenue / totalNetSalesWeightKg).toFixed(2) : '0.00'}/kg
                  </span>
                </p>
              </Card>
            </div>

            {/* Sales Lifts Table */}
            {sales.length === 0 ? (
              <div className="p-8 text-center border border-dashed border-slate-200 rounded-lg bg-slate-50/50">
                <Tag className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="font-medium text-slate-700">No Sales Recorded for this Cycle</p>
                <p className="text-xs text-slate-500 max-w-md mx-auto mt-1">
                  Log sales lifts when pigs are weighed at the chute for buyer checkout.
                </p>
                {isOperations && (
                  <Button
                    size="sm"
                    onClick={() => setShowPigSaleModal(true)}
                    className="mt-3 bg-emerald-700 hover:bg-emerald-600 text-xs text-white"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Log First Pig Sale Lift
                  </Button>
                )}
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-2xs">
                <table className="w-full text-xs text-left border-collapse">
                  <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3 w-8"></th>
                      <th className="py-2.5 px-3">Date</th>
                      <th className="py-2.5 px-3">Buyer</th>
                      <th className="py-2.5 px-3">Category</th>
                      <th className="py-2.5 px-3 text-right">Head</th>
                      <th className="py-2.5 px-3 text-right">Gross kg</th>
                      <th className="py-2.5 px-3 text-right text-amber-800">Shrink</th>
                      <th className="py-2.5 px-3 text-right font-bold text-emerald-800">Net kg</th>
                      <th className="py-2.5 px-3 text-right">Price/kg</th>
                      <th className="py-2.5 px-3 text-right font-bold text-slate-900">Total (₱)</th>
                      <th className="py-2.5 px-3">Ticket / Gate Pass</th>
                      <th className="py-2.5 px-3 text-center">Photos</th>
                      <th className="py-2.5 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sales.map((s) => {
                      const isExpanded = !!expandedSales[s.id];
                      const items = s.pig_sale_items || [];
                      const docCount = s.documents?.length || 0;

                      return (
                        <React.Fragment key={s.id}>
                          <tr className="hover:bg-slate-50/70 transition-colors">
                            <td className="py-2 px-2 text-center">
                              {items.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedSales((prev) => ({ ...prev, [s.id]: !prev[s.id] }))
                                  }
                                  className="p-1 text-slate-400 hover:text-slate-700"
                                  title="Toggle Scale Lines"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-3.5 h-3.5" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              )}
                            </td>
                            <td className="py-2 px-3 font-mono text-slate-700 whitespace-nowrap">{s.sale_date}</td>
                            <td className="py-2 px-3 font-medium text-slate-900">
                              {s.buyers?.buyer_name || s.legacy_raiser_name || 'Buyer'}
                            </td>
                            <td className="py-2 px-3 text-slate-600 font-mono text-[11px]">
                              {s.transaction_categories?.code ? `${s.transaction_categories.code}` : '1010'}
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-slate-800">
                              {s.head_count} hd
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-slate-700">
                              <TabularNumber value={Number(s.gross_weight_kg)} decimals={2} />
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-amber-800 bg-amber-50/20">
                              -<TabularNumber value={Number(s.shrinkage_deduction_kg)} decimals={2} />
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-emerald-800 bg-emerald-50/20">
                              <TabularNumber value={Number(s.net_weight_kg)} decimals={2} />
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-slate-600">
                              ₱{Number(s.price_per_kg).toFixed(2)}
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700">
                              <TabularNumber value={Number(s.total_amount)} type="currency" decimals={2} />
                            </td>
                            <td className="py-2 px-3 font-mono text-[11px] text-slate-600 whitespace-nowrap">
                              <div>{s.scale_ticket_no || s.sale_number || '—'}</div>
                              {s.gate_pass_no && (
                                <div className="text-[10px] text-slate-400">GP: {s.gate_pass_no}</div>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {docCount > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => s.documents && setPreviewDoc(s.documents[0])}
                                  className="inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:text-indigo-900 font-semibold px-1.5 py-0.5 rounded bg-indigo-50 hover:bg-indigo-100"
                                >
                                  <Camera className="w-3 h-3" /> {docCount}
                                </button>
                              ) : (
                                <span className="text-slate-300 text-[10px]">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setSelectedSaleForItems(s)}
                                  className="h-6 px-1.5 text-xs text-slate-600 hover:text-slate-900"
                                  title="View Scale Items"
                                >
                                  <Eye className="w-3 h-3 mr-0.5" /> Items
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setSelectedSaleForPrint(s)}
                                  className="h-6 px-1.5 text-xs text-emerald-800 border-emerald-300 hover:bg-emerald-50"
                                  title="Print Scale Slip"
                                >
                                  <Printer className="w-3 h-3 mr-0.5" /> Slip
                                </Button>
                                {isOperations && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      setVoidModalState({
                                        isOpen: true,
                                        tableName: 'pig_sales',
                                        recordId: s.id,
                                        recordIdentifier: `${s.sale_number || s.scale_ticket_no || 'Sale'} (${s.head_count} head)`,
                                      })
                                    }
                                    className="h-6 px-1 text-xs text-red-600 hover:bg-red-50"
                                    title="Soft-Void Sale"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Inline Expanded Scale Items Row */}
                          {isExpanded && items.length > 0 && (
                            <tr className="bg-slate-50/80">
                              <td colSpan={13} className="p-3 pl-8">
                                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white max-h-52 overflow-y-auto no-scrollbar">
                                  <table className="w-full text-[11px] text-left border-collapse">
                                    <thead className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200 sticky top-0">
                                      <tr>
                                        <th className="py-1.5 px-2.5 w-10 text-center">#</th>
                                        <th className="py-1.5 px-2.5 text-right">Gross Wt (kg)</th>
                                        <th className="py-1.5 px-2.5 text-right text-amber-800">Shrink (kg)</th>
                                        <th className="py-1.5 px-2.5 text-right font-bold text-emerald-800">Net Wt (kg)</th>
                                        <th className="py-1.5 px-2.5">Ear Tag</th>
                                        <th className="py-1.5 px-2.5">Notes</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-mono">
                                      {items.map((it: any) => (
                                        <tr key={it.id || it.sequence_no} className="hover:bg-slate-50">
                                          <td className="py-1 px-2.5 text-center text-slate-500">{it.sequence_no}</td>
                                          <td className="py-1 px-2.5 text-right font-medium text-slate-800">
                                            <TabularNumber value={Number(it.gross_weight_kg)} decimals={2} />
                                          </td>
                                          <td className="py-1 px-2.5 text-right text-amber-700 bg-amber-50/20">
                                            <TabularNumber value={Number(it.shrinkage_kg || s.shrinkage_rate_applied)} decimals={2} />
                                          </td>
                                          <td className="py-1 px-2.5 text-right font-bold text-emerald-800 bg-emerald-50/20">
                                            <TabularNumber value={Number(it.net_weight_kg)} decimals={2} />
                                          </td>
                                          <td className="py-1 px-2.5 text-slate-600 font-sans">{it.tag_no || '—'}</td>
                                          <td className="py-1 px-2.5 text-slate-500 font-sans">{it.notes || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab 7: Documents */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">Attached Documents & Photos</h3>
                <p className="text-xs text-slate-500">
                  Paper weighing sheets, receipts, and health test attachments stored in private Supabase bucket.
                </p>
              </div>
            </div>

            {isOperations && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">Upload New Attachment</h4>
                <DocumentUploader
                  entityType="production_cycle"
                  entityId={cycle.id}
                  onUploadSuccess={() => loadData()}
                />
              </div>
            )}
          </div>
        )}

        {/* Tab 8: Settlement & Liquidation */}
        {activeTab === 'settlement' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-slate-200">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-slate-900">Grower Settlement & Liquidation</h3>
                  {!settlementSummary?.isCompanyOwned && (
                    <StatusBadge status={settlementSummary?.status || 'draft'} category="settlement" />
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Interactive liquidation calculation, contract profit split, and finalized settlement liquidation statements.
                </p>
              </div>

              {!settlementSummary?.isCompanyOwned && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/cycles/${cycle.id}/settlement`)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold h-8 px-3 shadow-sm"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Open Settlement Workspace
                  </Button>
                </div>
              )}
            </div>

            {settlementSummary ? (
              <div className="space-y-4">
                {/* Status Callout Banner */}
                {settlementSummary.isCompanyOwned ? (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-200 rounded-lg text-slate-700 font-bold text-xs">
                        COMPANY-OWNED
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">
                          Company-Owned Production Cycle
                        </p>
                        <p className="text-[11px] text-slate-500">
                          Company-owned operations do not require a contract grower split liquidation.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : settlementSummary.isFrozen ? (
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-600 rounded-lg text-white">
                        <Lock className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-indigo-950">
                          Settlement Finalized & Locked
                        </p>
                        <p className="text-[11px] text-indigo-700">
                          Finalized on {settlementSummary.settlement_date ? formatDate(settlementSummary.settlement_date) : 'N/A'}. Associated Production Cycle is Settled.
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/cycles/${cycle.id}/settlement?print=1`)}
                      variant="outline"
                      className="text-xs bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                    >
                      <Printer className="w-3.5 h-3.5 mr-1.5" /> View Liquidation Statement
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-500 rounded-lg text-white">
                        <FileSpreadsheet className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-amber-950">
                          Live Settlement Draft (In Progress)
                        </p>
                        <p className="text-[11px] text-amber-700">
                          Calculated from live pig sales, arrivals, feed, and expenses. Open the workspace to finalize.
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/cycles/${cycle.id}/settlement`)}
                      className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
                    >
                      Manage Settlement
                    </Button>
                  </div>
                )}

                {/* 4 Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <span className="text-xs text-slate-500 font-medium block mb-1">Total Sales Revenue</span>
                      <span className="font-mono text-lg font-bold text-slate-900">
                        {formatCurrency(settlementSummary.total_sales_revenue)}
                      </span>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <span className="text-xs text-slate-500 font-medium block mb-1">Gross Operating Profit</span>
                      <span className="font-mono text-lg font-bold text-slate-900">
                        {formatCurrency(settlementSummary.gross_profit)}
                      </span>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <span className="text-xs text-slate-500 font-medium block mb-1">
                        Contract Split ({settlementSummary.grower_share_pct}% / {settlementSummary.vsb_share_pct}%)
                      </span>
                      <span className="font-mono text-lg font-bold text-slate-900">
                        {formatCurrency(settlementSummary.grower_gross_share)}
                      </span>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-200 shadow-sm bg-indigo-50/50 border-indigo-100">
                    <CardContent className="p-4">
                      <span className="text-xs text-indigo-900 font-bold block mb-1">Grower Net Payout</span>
                      <span className="font-mono text-xl font-black text-indigo-900">
                        {formatCurrency(settlementSummary.grower_net_payout)}
                      </span>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center border border-dashed border-slate-200 rounded-lg bg-slate-50/50 space-y-3">
                <FileSpreadsheet className="w-8 h-8 text-slate-300 mx-auto" />
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Settlement Active (No Draft Started)</p>
                  <p className="text-xs text-slate-500 max-w-md mx-auto mt-1">
                    Calculate live liquidation, bind grower contract profit split, manage post-profit adjustments, and finalize statements in the Settlement Workspace.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate(`/cycles/${cycle.id}/settlement`)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Launch Settlement Workspace
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Embedded Action Modals */}
      {showArrivalModal && (
        <PigArrivalModal
          isOpen={true}
          onClose={() => setShowArrivalModal(false)}
          onSuccess={() => loadData()}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
        />
      )}

      {showMortalityModal && (
        <MortalityLogModal
          isOpen={true}
          onClose={() => setShowMortalityModal(false)}
          onSuccess={() => loadData()}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
          currentLiveHeadcount={headcount.expectedCurrentHeadcount}
        />
      )}

      {showTransferModal && (
        <PigTransferModal
          isOpen={true}
          onClose={() => setShowTransferModal(false)}
          onSuccess={() => loadData()}
          defaultCycleId={cycle.id}
          defaultCycleCode={cycle.cycle_code}
          sourceLiveHeadcount={headcount.expectedCurrentHeadcount}
        />
      )}

      {showStatusModal && (
        <CycleStatusModal
          isOpen={true}
          onClose={() => setShowStatusModal(false)}
          onSuccess={() => loadData()}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
          currentStatus={cycle.status}
        />
      )}

      {showDirectFeedModal && (
        <FeedDeliveryModal
          isOpen={true}
          onClose={() => setShowDirectFeedModal(false)}
          onSuccess={() => loadData()}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
          farmId={cycle.farm_id}
        />
      )}

      {showBulkFeedModal && (
        <BulkFeedInvoiceModal
          isOpen={true}
          onClose={() => setShowBulkFeedModal(false)}
          onSuccess={() => loadData()}
          initialCycleId={cycle.id}
        />
      )}

      {showFeedTransferModal && (
        <FeedTransferModal
          isOpen={true}
          onClose={() => setShowFeedTransferModal(false)}
          onSuccess={() => loadData()}
          initialSourceCycleId={cycle.id}
        />
      )}

      {showHealthModal && (
        <HealthEventModal
          isOpen={true}
          onClose={() => setShowHealthModal(false)}
          onSuccess={() => loadData()}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
        />
      )}

      {showMedicineModal && (
        <MedicineReceiptModal
          isOpen={true}
          onClose={() => setShowMedicineModal(false)}
          onSuccess={() => loadData()}
          cycleId={cycle.id}
          farmId={cycle.farm_id}
        />
      )}

      {showExpenseModal && (
        <CycleExpenseModal
          isOpen={true}
          onClose={() => setShowExpenseModal(false)}
          onSuccess={() => loadData()}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
          farmId={cycle.farm_id}
        />
      )}

      {showPigSaleModal && (
        <PigSaleModal
          isOpen={true}
          onClose={() => setShowPigSaleModal(false)}
          onSuccess={(newSale) => {
            setShowPigSaleModal(false);
            loadData();
            setSelectedSaleForPrint(newSale);
          }}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
        />
      )}

      <ScaleSlipPrintModal
        isOpen={!!selectedSaleForPrint}
        onClose={() => setSelectedSaleForPrint(null)}
        sale={selectedSaleForPrint}
      />

      <ScaleItemsDetailModal
        isOpen={!!selectedSaleForItems}
        onClose={() => setSelectedSaleForItems(null)}
        sale={selectedSaleForItems}
        onPrintSlip={() => {
          setSelectedSaleForPrint(selectedSaleForItems);
          setSelectedSaleForItems(null);
        }}
      />

      <SoftVoidConfirmModal
        isOpen={voidModalState.isOpen}
        onClose={() => setVoidModalState({ isOpen: false, tableName: '', recordId: '', recordIdentifier: '' })}
        onConfirm={handleSoftVoidConfirm}
        recordIdentifier={voidModalState.recordIdentifier}
      />

      {previewDoc && (
        <DocumentPreviewModal
          isOpen={true}
          onClose={() => setPreviewDoc(null)}
          document={previewDoc}
        />
      )}
    </div>
  );
};
